import { Button, Divider, Tab, Tabs, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import useCompartmentGenerator from '../Providers/CompartmentGeneratorProvider';
import { CenteredColumn, ReactiveRow } from 'src/Common/Components/Stacks';
import RegexInput from 'src/Common/Components/Forms/RegexInput';
import { Add } from '@mui/icons-material';

interface Props {
  /** sets a default input value for the field name input. */
  initialFieldInputValue?: string;
}

export const CompartmentGeneratorForm = ({ initialFieldInputValue }: Props): React.JSX.Element => {
  const { fieldTemplates, fieldKeys, setField, removeField } = useCompartmentGenerator();

  const [currentTab, setCurrentTab] = useState<number>(0);
  const [fieldInput, setFieldInput] = useState<string>(initialFieldInputValue ?? '');

  const currentField = useMemo(() => fieldKeys.at(currentTab), [fieldKeys, currentTab]);

  const fieldForm = useMemo(() => {
    if (currentField === undefined) {
      return <></>;
    }
    const { replacement, pattern } = fieldTemplates[currentField];

    return (
      <RegexInput
        label={'Pattern'}
        value={pattern ?? ''}
        replacement={replacement ?? ''}
        onChange={(value) => setField(currentField, { pattern: value, replacement })}
        onChangeReplacement={(value) => setField(currentField, { pattern, replacement: value })}
      />
    );
  }, [fieldKeys, currentField]);

  const isAddDisabled = useMemo(() => fieldInput === '' || fieldKeys.includes(fieldInput), [fieldInput, fieldKeys]);

  return (
    <CenteredColumn width={'100%'} spacing={1.5}>
      <Tabs
        value={currentTab}
        variant={'scrollable'}
        scrollButtons={'auto'}
        onChange={(_, newTab) => setCurrentTab(newTab)}
      >
        {fieldKeys.map((k: string, index) => (
          <Tab
            key={`tGroup-${index}`}
            label={<Typography color={'secondary'}>{k}</Typography>}
            style={{ textTransform: 'none' }}
          />
        ))}
      </Tabs>
      <Divider />
      {fieldForm}
      <ReactiveRow width={'100%'}>
        <StyledTextField
          type={'text'}
          label={'Field'}
          value={fieldInput}
          onChange={(e) => setFieldInput(String(e.target.value))}
          action={{
            icon: Add,
            onClick: () => {
              setField(fieldInput, { pattern: '', replacement: '' });
              setFieldInput('');
            },
            disabled: isAddDisabled,
          }}
        />
        <Button
          variant={'outlined'}
          color={'error'}
          disabled={currentField === undefined || currentField === 'compartment'}
          onClick={() => removeField(currentField!)}
        >
          remove
        </Button>
      </ReactiveRow>
    </CenteredColumn>
  );
};
